import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import AdminLayout from '../layouts/AdminLayout';
import { db } from '../firebase';
import {
  Typography, LinearProgress, makeStyles, Snackbar, Grid, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import GetAppIcon from '@material-ui/icons/GetApp';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import SystemUpdateAltIcon from '@material-ui/icons/SystemUpdateAlt';
import DeleteSweepIcon from '@material-ui/icons/DeleteSweep';

const useStyles = makeStyles((theme) => ({
  container: {
    maxWidth: 900,
    margin: '0 auto'
  },
  header: {
    marginBottom: theme.spacing(4)
  },
  title: {
    fontWeight: 700,
    fontSize: '1.5rem',
    color: theme.palette.text.primary,
    fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif'
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginTop: 8
  },
  actionCard: {
    padding: theme.spacing(4),
    textAlign: 'center',
    cursor: 'pointer',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 20,
    transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover:not($disabledCard)': {
      transform: 'translateY(-4px)',
      background: theme.palette.type === "dark" ? "rgba(30,41,59,0.8)" : "rgba(255,255,255,0.8)",
      borderColor: 'rgba(16,185,129,0.3)',
      boxShadow: '0 12px 32px rgba(0,0,0,0.3), 0 0 0 1px rgba(16,185,129,0.1)',
      '& $iconWrapper': {
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        color: '#fff',
        boxShadow: '0 8px 24px rgba(16,185,129,0.4)',
        transform: 'scale(1.05)'
      }
    }
  },
  disabledCard: {
    opacity: 0.6,
    cursor: 'not-allowed'
  },
  iconWrapper: {
    width: 72,
    height: 72,
    borderRadius: 20,
    background: 'rgba(148,163,184,0.06)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
    marginBottom: 20,
    transition: 'all 0.3s ease',
    '& svg': { fontSize: 32 }
  },
  cardTitle: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: 8,
    fontSize: '1.1rem'
  },
  cardDesc: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
    lineHeight: 1.5
  },
  progress: {
    marginTop: theme.spacing(6),
    padding: theme.spacing(4),
    background: theme.palette.background.paper,
    borderRadius: 20,
    border: `1px solid ${theme.palette.divider}`
  },
  status: {
    marginTop: theme.spacing(2),
    color: '#10b981',
    fontWeight: 500,
    textAlign: 'center'
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    backgroundColor: 'rgba(16,185,129,0.1)',
    '& .MuiLinearProgress-bar': {
      background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
      borderRadius: 4
    }
  },
  fileInputLabel: {
    display: 'block',
    margin: '12px 0 6px 0',
    fontWeight: 600,
    fontSize: '0.9rem',
    color: theme.palette.text.primary
  },
  fileInput: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.default,
    color: theme.palette.text.primary
  }
}));

const BACKUP_COLLECTIONS = [
  'dua_categories',
  'dua_subcategories',
  'duas',
  'hadith_categories',
  'hadith_books',
  'hadith_topics',
  'hadith',
  'quotes',
  'prophet_stories'
];

export default function MaintenancePage() {
  const classes = useStyles();
  const history = useHistory();
  
  const [processing, setProcessing] = useState(false);
  const [status, setStatus] = useState('');
  const [progress, setProgress] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  
  const [isMigrateOpen, setIsMigrateOpen] = useState(false);
  const [isRestoreOpen, setIsRestoreOpen] = useState(false);
  const [isProphetsImportOpen, setIsProphetsImportOpen] = useState(false);

  // File states
  const [categoriesFile, setCategoriesFile] = useState(null);
  const [subcategoriesFile, setSubcategoriesFile] = useState(null);
  const [duasFile, setDuasFile] = useState(null);
  const [restoreFile, setRestoreFile] = useState(null);
  const [prophetsFile, setProphetsFile] = useState(null);

  const deleteCollection = async (collectionName) => {
    const snapshot = await db.collection(collectionName).get();
    const batchSize = 400;
    let batch = db.batch();
    let count = 0;
    for (const doc of snapshot.docs) {
      if (collectionName === 'prophet_stories') {
        const chSnapshot = await doc.ref.collection('chapters').get();
        let chBatch = db.batch();
        chSnapshot.docs.forEach(chDoc => chBatch.delete(chDoc.ref));
        await chBatch.commit();
      }
      batch.delete(doc.ref);
      count++;
      if (count >= batchSize) {
        await batch.commit();
        batch = db.batch();
        count = 0;
      }
    }
    if (count > 0) {
      await batch.commit();
    }
  };

  const handleReserialize = async () => {
    if (processing) return;
    setProcessing(true);
    setStatus('Starting database reserialization...');
    setProgress(0);
    try {
      const collections = ['duas', 'hadith', 'quotes'];
      let totalDocs = 0;

      for (const collection of collections) {
        const snapshot = await db.collection(collection).orderBy('orderIndex').get();
        totalDocs += snapshot.size;
      }

      let processed = 0;
      for (const collection of collections) {
        const snapshot = await db.collection(collection).orderBy('orderIndex').get();
        let idx = 0;
        const batch = db.batch();
        let batchCount = 0;

        for (const doc of snapshot.docs) {
          const data = doc.data();
          batch.update(doc.ref, { ...data, orderIndex: idx, updatedAt: new Date() });
          idx++;
          batchCount++;
          processed++;

          if (batchCount >= 500) {
            await batch.commit();
            batchCount = 0;
          }

          setProgress(Math.round((processed / totalDocs) * 100));
          setStatus(`Optimizing ${collection} (${processed}/${totalDocs})`);
        }

        if (batchCount > 0) {
          await batch.commit();
        }
      }

      setStatus('Optimization complete! System is running at peak performance.');
      setSnackbar({ open: true, message: 'Database optimized successfully' });
      setTimeout(() => setProcessing(false), 2000);
    } catch (err) {
      setStatus('Error: ' + err.message);
      setSnackbar({ open: true, message: 'Failed to optimize database' });
      setProcessing(false);
    }
  };

  const handleExport = async () => {
    if (processing) return;
    setProcessing(true);
    setStatus('Preparing secure full system backup...');
    setProgress(10);
    try {
      const backupData = {};
      const totalCollections = BACKUP_COLLECTIONS.length;

      for (let i = 0; i < totalCollections; i++) {
        const colName = BACKUP_COLLECTIONS[i];
        setStatus(`Exporting ${colName}...`);
        setProgress(Math.round(10 + (i / totalCollections) * 80));

        if (colName === 'prophet_stories') {
          const snapshot = await db.collection(colName).get();
          const prophets = [];
          for (const doc of snapshot.docs) {
            const data = doc.data();
            const chSnapshot = await doc.ref.collection('chapters').get();
            const chapters = chSnapshot.docs.map(chDoc => ({ id: chDoc.id, ...chDoc.data() }));
            prophets.push({
              id: doc.id,
              ...data,
              chapters
            });
          }
          backupData[colName] = prophets;
        } else {
          const snapshot = await db.collection(colName).get();
          backupData[colName] = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        }
      }

      const exportData = {
        ...backupData,
        exportedAt: new Date().toISOString(),
        version: '1.0.0'
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `noor_full_system_backup_${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setStatus('Export complete! Full system backup downloaded.');
      setSnackbar({ open: true, message: 'Backup downloaded successfully' });
      setProgress(100);
      setTimeout(() => setProcessing(false), 2000);
    } catch (err) {
      console.error(err);
      setStatus('Error: ' + err.message);
      setSnackbar({ open: true, message: 'Failed to create backup' });
      setProcessing(false);
    }
  };

  const handleAddTimestamps = async () => {
    if (processing) return;
    setProcessing(true);
    setStatus('Adding timestamps to all documents...');
    setProgress(0);
    try {
      const collections = [
        'duas', 'dua_categories', 'dua_subcategories', 
        'hadith', 'hadith_categories', 'hadith_books', 'hadith_topics', 
        'prophet_stories', 'quotes'
      ];
      let totalDocs = 0;

      for (const collection of collections) {
        const snapshot = await db.collection(collection).get();
        totalDocs += snapshot.size;
      }

      let processed = 0;
      for (const collection of collections) {
        const snapshot = await db.collection(collection).get();
        let batch = db.batch();
        let batchCount = 0;

        for (const doc of snapshot.docs) {
          batch.update(doc.ref, {
            created_at: new Date(),
            updated_at: "",
            deleted_at: ""
          });
          batchCount++;
          processed++;

          if (batchCount >= 400) {
            await batch.commit();
            batch = db.batch();
            batchCount = 0;
          }

          setProgress(Math.round((processed / totalDocs) * 100));
          setStatus(`Updating ${collection} (${processed}/${totalDocs})`);
        }

        if (batchCount > 0) {
          await batch.commit();
        }
      }

      setStatus('Timestamps added successfully!');
      setSnackbar({ open: true, message: 'Timestamps added successfully' });
      setTimeout(() => setProcessing(false), 2000);
    } catch (err) {
      setStatus('Error: ' + err.message);
      setSnackbar({ open: true, message: 'Failed to add timestamps' });
      setProcessing(false);
    }
  };

  const handleDeletePermanently = async () => {
    if (processing) return;
    setProcessing(true);
    setStatus('Deleting permanently deleted data...');
    setProgress(0);
    try {
      const collections = [
        'duas', 'dua_categories', 'dua_subcategories', 
        'hadith', 'hadith_categories', 'hadith_books', 'hadith_topics', 
        'prophet_stories', 'quotes'
      ];
      let totalDocs = 0;
      
      for (const collection of collections) {
        const snapshot = await db.collection(collection).get();
        totalDocs += snapshot.size;
      }

      let processed = 0;
      let deletedCount = 0;
      for (const collection of collections) {
        const snapshot = await db.collection(collection).get();
        let batch = db.batch();
        let batchCount = 0;

        for (const doc of snapshot.docs) {
          const data = doc.data();
          if (data.deleted_at !== undefined && data.deleted_at !== "" && data.deleted_at !== null) {
            batch.delete(doc.ref);
            batchCount++;
            deletedCount++;
          }
          processed++;

          if (batchCount >= 400) {
            await batch.commit();
            batch = db.batch();
            batchCount = 0;
          }

          setProgress(Math.round((processed / totalDocs) * 100));
          setStatus(`Checking ${collection} (${processed}/${totalDocs})`);
        }

        if (batchCount > 0) {
          await batch.commit();
        }
      }

      setStatus(`Deleted ${deletedCount} documents permanently!`);
      setSnackbar({ open: true, message: 'Deleted data permanently' });
      setTimeout(() => setProcessing(false), 2000);
    } catch (err) {
      setStatus('Error: ' + err.message);
      setSnackbar({ open: true, message: 'Failed to delete data' });
      setProcessing(false);
    }
  };

  const readJsonFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          resolve(JSON.parse(e.target.result));
        } catch (err) {
          reject(new Error(`Failed to parse JSON in ${file.name}`));
        }
      };
      reader.onerror = () => reject(new Error(`Failed to read file ${file.name}`));
      reader.readAsText(file);
    });
  };

  const handleStartRestore = async () => {
    if (!restoreFile) {
      setSnackbar({ open: true, message: 'Please select a backup JSON file.' });
      return;
    }
    setIsRestoreOpen(false);
    setProcessing(true);
    setStatus('Reading backup file...');
    setProgress(5);

    try {
      const backupJson = await readJsonFile(restoreFile);
      
      const requiredCollections = ['duas', 'hadith', 'quotes', 'prophet_stories'];
      const hasRequired = requiredCollections.every(col => Array.isArray(backupJson[col]));
      if (!hasRequired) {
        throw new Error('Invalid backup file structure: missing core collections.');
      }

      setStatus('Deleting existing database collections...');
      setProgress(15);
      for (let i = 0; i < BACKUP_COLLECTIONS.length; i++) {
        const colName = BACKUP_COLLECTIONS[i];
        setStatus(`Clearing ${colName}...`);
        setProgress(Math.round(15 + (i / BACKUP_COLLECTIONS.length) * 35));
        await deleteCollection(colName);
      }

      setStatus('Importing restored data...');
      setProgress(50);

      const totalCollections = BACKUP_COLLECTIONS.length;
      for (let i = 0; i < totalCollections; i++) {
        const colName = BACKUP_COLLECTIONS[i];
        const docs = backupJson[colName] || [];
        setStatus(`Restoring ${colName} (${docs.length} items)...`);
        setProgress(Math.round(50 + (i / totalCollections) * 45));

        if (docs.length === 0) continue;

        let batch = db.batch();
        let count = 0;

        for (let j = 0; j < docs.length; j++) {
          const item = docs[j];
          const { id, ...data } = item;

          if (colName === 'prophet_stories') {
            const { chapters, ...prophetData } = data;
            const docRef = db.collection(colName).doc(id);
            batch.set(docRef, prophetData);
            count++;

            if (Array.isArray(chapters) && chapters.length > 0) {
              for (const ch of chapters) {
                const { id: chId, ...chData } = ch;
                const chRef = docRef.collection('chapters').doc(chId);
                if (count >= 400) {
                  await batch.commit();
                  batch = db.batch();
                  count = 0;
                }
                batch.set(chRef, chData);
                count++;
              }
            }
          } else {
            const docRef = db.collection(colName).doc(id);
            batch.set(docRef, data);
            count++;
          }

          if (count >= 400) {
            await batch.commit();
            batch = db.batch();
            count = 0;
          }
        }

        if (count > 0) {
          await batch.commit();
        }
      }

      setProgress(100);
      setStatus('System restore complete! All collections restored successfully.');
      setSnackbar({ open: true, message: 'System restored successfully!' });
      setTimeout(() => setProcessing(false), 3000);
    } catch (err) {
      console.error(err);
      setStatus('Restore Failed: ' + err.message);
      setSnackbar({ open: true, message: 'Failed to restore system backup' });
      setProcessing(false);
    }
  };

  const handleStartProphetsImport = async () => {
    if (!prophetsFile) {
      setSnackbar({ open: true, message: 'Please select a prophets JSON file.' });
      return;
    }
    setIsProphetsImportOpen(false);
    setProcessing(true);
    setStatus('Reading prophets JSON file...');
    setProgress(10);

    try {
      const jsonData = await readJsonFile(prophetsFile);
      const prophetsArray = Array.isArray(jsonData) ? jsonData : (jsonData.prophets || [jsonData]);

      setStatus('Deleting existing prophets and chapters...');
      setProgress(30);
      await deleteCollection('prophet_stories');

      setStatus('Importing new prophets and chapters...');
      setProgress(60);

      let batch = db.batch();
      let count = 0;

      for (let i = 0; i < prophetsArray.length; i++) {
        const prophet = prophetsArray[i];
        const prophetRef = db.collection('prophet_stories').doc();
        const { chapters, ...prophetData } = prophet;

        batch.set(prophetRef, {
          arabicName: prophetData.arabicName || '',
          name: prophetData.name || { en: prophetData.englishName || '', bn: prophetData.banglaName || '' },
          description: prophetData.description || { en: prophetData.englishTitle || '', bn: prophetData.banglaTitle || '' },
          imageUrl: prophetData.imageUrl || '',
          orderIndex: prophetData.orderIndex !== undefined ? prophetData.orderIndex : i,
          createdAt: new Date(),
          updatedAt: new Date(),
          created_at: new Date(),
          updated_at: "",
          deleted_at: "",
        });
        count++;

        if (Array.isArray(chapters) && chapters.length > 0) {
          for (let j = 0; j < chapters.length; j++) {
            const ch = chapters[j];
            const chRef = prophetRef.collection('chapters').doc();
            
            if (count >= 400) {
              await batch.commit();
              batch = db.batch();
              count = 0;
            }

            batch.set(chRef, {
              title: ch.title || { en: ch.englishTitle || '', bn: ch.banglaTitle || '' },
              content: ch.content || { en: ch.englishContent || '', bn: ch.banglaContent || '' },
              orderIndex: ch.orderIndex !== undefined ? ch.orderIndex : j,
              createdAt: new Date(),
              updatedAt: new Date(),
            });
            count++;
          }
        }

        if (count >= 400) {
          await batch.commit();
          batch = db.batch();
          count = 0;
        }
      }

      if (count > 0) {
        await batch.commit();
      }

      setProgress(100);
      setStatus('Prophets import complete! All prophets and chapters imported successfully.');
      setSnackbar({ open: true, message: 'Prophets imported successfully!' });
      setTimeout(() => setProcessing(false), 3000);
    } catch (err) {
      console.error(err);
      setStatus('Import Failed: ' + err.message);
      setSnackbar({ open: true, message: 'Failed to import prophets' });
      setProcessing(false);
    }
  };

  const handleStartMigration = async () => {
    if (!categoriesFile || !subcategoriesFile || !duasFile) {
      setSnackbar({ open: true, message: 'Please select all three JSON files.' });
      return;
    }
    setIsMigrateOpen(false);
    setProcessing(true);
    setStatus('Reading JSON files...');
    setProgress(5);

    try {
      const categoriesJson = await readJsonFile(categoriesFile);
      const subCategoriesJson = await readJsonFile(subcategoriesFile);
      const duasJson = await readJsonFile(duasFile);

      setStatus('Mapping data relationships...');
      setProgress(15);

      const duaToSub = {};
      subCategoriesJson.forEach((sub) => {
        const subId = `sub_${sub.id}`;
        if (Array.isArray(sub['dua-ids'])) {
          sub['dua-ids'].forEach((duaId) => {
            duaToSub[duaId] = {
              subCategoryId: subId,
              categoryId: sub.category
            };
          });
        }
      });

      setStatus('Importing Categories...');
      setProgress(25);
      let catBatch = db.batch();
      let catCount = 0;
      for (let i = 0; i < categoriesJson.length; i++) {
        const cat = categoriesJson[i];
        const docRef = db.collection('dua_categories').doc(cat.url);
        catBatch.set(docRef, {
          nameBn: cat.name,
          nameEn: cat.url.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          icon: cat.icon || '',
          orderIndex: i,
          isDeleted: false
        }, { merge: true });
        catCount++;
        if (catCount >= 400) {
          await catBatch.commit();
          catBatch = db.batch();
          catCount = 0;
        }
      }
      if (catCount > 0) await catBatch.commit();

      setStatus('Importing Subcategories...');
      setProgress(50);
      let subBatch = db.batch();
      let subCount = 0;
      for (let i = 0; i < subCategoriesJson.length; i++) {
        const sub = subCategoriesJson[i];
        const subId = `sub_${sub.id}`;
        const docRef = db.collection('dua_subcategories').doc(subId);
        subBatch.set(docRef, {
          titleBn: sub.title,
          titleEn: sub.title,
          categoryId: sub.category,
          orderIndex: i,
          isDeleted: false
        }, { merge: true });
        subCount++;
        if (subCount >= 400) {
          await subBatch.commit();
          subBatch = db.batch();
          subCount = 0;
        }
      }
      if (subCount > 0) await subBatch.commit();

      setStatus('Importing Duas (this might take a few moments)...');
      let duaBatch = db.batch();
      let duaCount = 0;
      const totalDuas = duasJson.length;

      for (let i = 0; i < totalDuas; i++) {
        const d = duasJson[i];
        const docId = `dua_${d.dua_id}`;
        const docRef = db.collection('duas').doc(docId);
        
        const mapping = duaToSub[d.dua_id] || { subCategoryId: '', categoryId: 'other' };

        duaBatch.set(docRef, {
          title: {
            bn: d.title || '',
            en: d.title || ''
          },
          textArabic: d.arabic || '',
          transliteration: {
            bn: d.transliteration || '',
            en: d.transliteration || ''
          },
          translation: {
            bn: d.translation || '',
            en: d.translation || ''
          },
          category: mapping.categoryId,
          subCategoryId: mapping.subCategoryId,
          audioUrl: d.audio || '',
          reference: d.reference || '',
          introduction: d.introduction || '',
          isPublished: true,
          isDeleted: false,
          orderIndex: d.dua_id
        }, { merge: true });

        duaCount++;
        if (duaCount >= 400) {
          setStatus(`Importing Duas (${i + 1}/${totalDuas})...`);
          setProgress(Math.round(50 + (i / totalDuas) * 45));
          await duaBatch.commit();
          duaBatch = db.batch();
          duaCount = 0;
        }
      }
      if (duaCount > 0) await duaBatch.commit();

      setProgress(100);
      setStatus('Migration complete! All categories, subcategories, and duas imported successfully.');
      setSnackbar({ open: true, message: 'Dua database migrated successfully!' });
      setTimeout(() => setProcessing(false), 3000);
    } catch (err) {
      console.error(err);
      setStatus('Migration Failed: ' + err.message);
      setSnackbar({ open: true, message: 'Failed to migrate database' });
      setProcessing(false);
    }
  };

  return (
    <AdminLayout title="System Maintenance">
      <div className={classes.container}>
        <div className={classes.header}>
          <Typography className={classes.title}>System Tools & Utilities</Typography>
          <Typography className={classes.subtitle}>
            Advanced tools for database optimization and secure backups. Execute with caution.
          </Typography>
        </div>

        <Grid container spacing={4}>
          <Grid item xs={12} sm={4}>
            <Card 
              className={classes.actionCard} 
              onClick={() => history.push('/app-version')} 
              elevation={0}
              style={{ border: '1px solid rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.02)' }}
            >
              <CardContent style={{ padding: 0, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div className={classes.iconWrapper} style={{ color: '#10b981', background: 'rgba(16,185,129,0.1)' }}>
                  <SystemUpdateAltIcon />
                </div>
                <Typography className={classes.cardTitle} style={{ color: '#10b981' }}>App Version & Update</Typography>
                <Typography className={classes.cardDesc}>
                  Set required app version in Firebase Firestore and enforce unclosable update bottom sheet across apps.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card 
              className={`${classes.actionCard} ${processing ? classes.disabledCard : ''}`} 
              onClick={handleReserialize} 
              elevation={0}
            >
              <CardContent style={{ padding: 0, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div className={classes.iconWrapper}>
                  <RefreshIcon />
                </div>
                <Typography className={classes.cardTitle}>Optimize Database</Typography>
                <Typography className={classes.cardDesc}>
                  Re-index sequential data, update timestamps, and clean up orphaned records for faster querying.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card 
              className={`${classes.actionCard} ${processing ? classes.disabledCard : ''}`} 
              onClick={handleExport} 
              elevation={0}
            >
              <CardContent style={{ padding: 0, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div className={classes.iconWrapper}>
                  <GetAppIcon />
                </div>
                <Typography className={classes.cardTitle}>System Backup</Typography>
                <Typography className={classes.cardDesc}>
                  Generate a complete JSON backup of all database collections including prophets, chapters, duas, and hadith.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card 
              className={`${classes.actionCard} ${processing ? classes.disabledCard : ''}`} 
              onClick={() => !processing && setIsRestoreOpen(true)} 
              elevation={0}
            >
              <CardContent style={{ padding: 0, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div className={classes.iconWrapper}>
                  <SystemUpdateAltIcon />
                </div>
                <Typography className={classes.cardTitle}>Import System Backup</Typography>
                <Typography className={classes.cardDesc}>
                  Upload full system backup JSON to replace all database collections with backed-up data.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card 
              className={`${classes.actionCard} ${processing ? classes.disabledCard : ''}`} 
              onClick={() => !processing && setIsProphetsImportOpen(true)} 
              elevation={0}
            >
              <CardContent style={{ padding: 0, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div className={classes.iconWrapper}>
                  <CloudUploadIcon />
                </div>
                <Typography className={classes.cardTitle}>Import Prophets JSON</Typography>
                <Typography className={classes.cardDesc}>
                  Replace all existing prophets and chapters with data uploaded from a formatted prophets JSON file.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card 
              className={`${classes.actionCard} ${processing ? classes.disabledCard : ''}`} 
              onClick={() => !processing && setIsMigrateOpen(true)} 
              elevation={0}
            >
              <CardContent style={{ padding: 0, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div className={classes.iconWrapper}>
                  <DeleteSweepIcon />
                </div>
                <Typography className={classes.cardTitle}>Import Dua Database</Typography>
                <Typography className={classes.cardDesc}>
                  Upload categories, subcategories, and duas JSON to fully migrate and index Duas correctly.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card 
              className={`${classes.actionCard} ${processing ? classes.disabledCard : ''}`} 
              onClick={handleAddTimestamps} 
              elevation={0}
            >
              <CardContent style={{ padding: 0, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div className={classes.iconWrapper}>
                  <AccessTimeIcon />
                </div>
                <Typography className={classes.cardTitle}>Add Timestamps Fields</Typography>
                <Typography className={classes.cardDesc}>
                  Add created_at, updated_at, and deleted_at to all documents in the database.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card 
              className={`${classes.actionCard} ${processing ? classes.disabledCard : ''}`} 
              onClick={handleDeletePermanently} 
              elevation={0}
            >
              <CardContent style={{ padding: 0, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div className={classes.iconWrapper}>
                  <DeleteForeverIcon />
                </div>
                <Typography className={classes.cardTitle}>Delete all deleted data</Typography>
                <Typography className={classes.cardDesc}>
                  Permanently remove any data that has been soft-deleted (where deleted_at is not empty).
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {processing && (
          <div className={classes.progress}>
            <Typography variant="subtitle2" color="textPrimary" style={{ marginBottom: 12 }}>System Operation in Progress</Typography>
            <LinearProgress variant="determinate" value={progress} className={classes.progressBar} />
            <Typography className={classes.status}>{status}</Typography>
          </div>
        )}
      </div>

      {/* Migration Dialog */}
      <Dialog open={isMigrateOpen} onClose={() => setIsMigrateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Migrate Dua Database</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
              Upload the JSON files from your downloads. Existing records will be updated and merged securely without losing custom edits.
            </Typography>
          </Box>
          <Box mb={2}>
            <label className={classes.fileInputLabel}>1. Categories File (categories.json)</label>
            <input 
              type="file" 
              accept=".json"
              className={classes.fileInput} 
              onChange={(e) => setCategoriesFile(e.target.files[0])} 
            />
          </Box>
          <Box mb={2}>
            <label className={classes.fileInputLabel}>2. Subcategories File (sub_categories.json)</label>
            <input 
              type="file" 
              accept=".json"
              className={classes.fileInput} 
              onChange={(e) => setSubcategoriesFile(e.target.files[0])} 
            />
          </Box>
          <Box mb={2}>
            <label className={classes.fileInputLabel}>3. Duas File (duas.json)</label>
            <input 
              type="file" 
              accept=".json"
              className={classes.fileInput} 
              onChange={(e) => setDuasFile(e.target.files[0])} 
            />
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button onClick={() => setIsMigrateOpen(false)} color="secondary">Cancel</Button>
          <Button 
            onClick={handleStartMigration} 
            color="primary" 
            variant="contained"
            disabled={!categoriesFile || !subcategoriesFile || !duasFile}
          >
            Start Migration
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Backup Dialog */}
      <Dialog open={isRestoreOpen} onClose={() => setIsRestoreOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Import System Backup</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Typography variant="body2" color="secondary" style={{ fontWeight: 600, marginBottom: 12 }}>
              ⚠️ WARNING: This will permanently DELETE all current data in the database (duas, hadiths, quotes, prophets, and chapters) and replace it with the backed-up data.
            </Typography>
          </Box>
          <Box mb={2}>
            <label className={classes.fileInputLabel}>Select Backup JSON File</label>
            <input 
              type="file" 
              accept=".json"
              className={classes.fileInput} 
              onChange={(e) => setRestoreFile(e.target.files[0])} 
            />
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button onClick={() => setIsRestoreOpen(false)} color="secondary">Cancel</Button>
          <Button 
            onClick={handleStartRestore} 
            color="primary" 
            variant="contained"
            disabled={!restoreFile}
          >
            Restore Backup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Prophets Dialog */}
      <Dialog open={isProphetsImportOpen} onClose={() => setIsProphetsImportOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Import Prophets Database</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Typography variant="body2" color="secondary" style={{ fontWeight: 600, marginBottom: 12 }}>
              ⚠️ WARNING: This will permanently DELETE all current prophet stories and their chapters and replace them with the data uploaded from the JSON file.
            </Typography>
          </Box>
          <Box mb={2}>
            <label className={classes.fileInputLabel}>Select Prophets JSON File</label>
            <input 
              type="file" 
              accept=".json"
              className={classes.fileInput} 
              onChange={(e) => setProphetsFile(e.target.files[0])} 
            />
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button onClick={() => setIsProphetsImportOpen(false)} color="secondary">Cancel</Button>
          <Button 
            onClick={handleStartProphetsImport} 
            color="primary" 
            variant="contained"
            disabled={!prophetsFile}
          >
            Import Prophets
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        message={snackbar.message} 
        autoHideDuration={4000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })} 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </AdminLayout>
  );
}